import React, { useState, useEffect } from "react";
import {
  FaCheckCircle,
  FaExclamationCircle,
  FaSync,
  FaInfoCircle,
  FaExternalLinkAlt,
  FaEdit,
  FaSave,
  FaTimes,
} from "react-icons/fa";
import {
  checkAllFanpageTokens,
  reconnectFanpage,
  checkAndRefreshFanpageToken,
  grantAdditionalCommentPermissions,
} from "../services/tokenService";
import { FACEBOOK_COMMENT_REQUIRED_SCOPES } from "../utils/facebookCommentPermissions";
import ReconnectFanpageModal from "./ReconnectFanpageModal";
import { toast } from "react-toastify";
import { getDatabase, ref, set, get } from "firebase/database";

/**
 * Component quản lý token của các fanpage
 * @param {Array} connectedFanpages - Danh sách fanpage đã kết nối
 * @param {number} refreshTrigger - Giá trị để kích hoạt việc làm mới trạng thái fanpage
 * @returns {JSX.Element} - Component FanpageTokenManager
 */
const FanpageTokenManager = ({
  connectedFanpages = [],
  refreshTrigger = 0,
}) => {
  const [fanpageStatuses, setFanpageStatuses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showReconnectModal, setShowReconnectModal] = useState(false);
  const [selectedFanpage, setSelectedFanpage] = useState(null);
  const [showInfo, setShowInfo] = useState(false);
  const [processingFanpageId, setProcessingFanpageId] = useState(null);
  const [editingFanpageId, setEditingFanpageId] = useState(null);
  const [fanpageInfo, setFanpageInfo] = useState("");
  const [field, setField] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // State để theo dõi quá trình tự động kết nối
  const [autoConnectStats, setAutoConnectStats] = useState({
    total: 0,
    success: 0,
    failed: 0,
    inProgress: false,
  });

  // Kết hợp thông tin từ connectedFanpages và fanpageStatuses
  const [combinedFanpageData, setCombinedFanpageData] = useState([]);

  // Kết hợp dữ liệu từ connectedFanpages và fanpageStatuses
  useEffect(() => {
    if (connectedFanpages.length > 0 && fanpageStatuses.length > 0) {
      // Tạo map từ fanpageStatuses để dễ truy cập
      const statusMap = fanpageStatuses.reduce((map, status) => {
        map[status.id] = status;
        return map;
      }, {});

      // Kết hợp dữ liệu
      const combined = connectedFanpages.map((page) => {
        const status = statusMap[page.id] || { isValid: true };
        // Đảm bảo tokenStatus được đặt chính xác dựa trên isValid
        const tokenStatus =
          status.isValid === false
            ? "invalid"
            : page.tokenStatus === "active" || status.tokenStatus === "active"
              ? "active"
              : page.tokenStatus || "active";

        return {
          ...page,
          ...status,
          tokenStatus: tokenStatus,
        };
      });

      setCombinedFanpageData(combined);
    } else if (connectedFanpages.length > 0) {
      // Nếu chưa có fanpageStatuses, sử dụng connectedFanpages
      // Đảm bảo mỗi fanpage có tokenStatus
      const combined = connectedFanpages.map((page) => ({
        ...page,
        tokenStatus: page.tokenStatus || "active",
      }));
      setCombinedFanpageData(combined);
    } else if (fanpageStatuses.length > 0) {
      // Nếu chưa có connectedFanpages, sử dụng fanpageStatuses
      // Đảm bảo mỗi fanpage có tokenStatus dựa trên isValid
      const combined = fanpageStatuses.map((status) => ({
        ...status,
        tokenStatus: status.isValid === false ? "invalid" : "active",
      }));
      setCombinedFanpageData(combined);
    }
  }, [connectedFanpages, fanpageStatuses]);

  // Lấy thông tin fanpage khi chọn fanpage để chỉnh sửa
  useEffect(() => {
    if (editingFanpageId) {
      const fetchFanpageData = async () => {
        try {
          const userId = localStorage.getItem("userId");
          const db = getDatabase();
          const fanpageRef = ref(
            db,
            `users/${userId}/fanpages/${editingFanpageId}`,
          );
          const snapshot = await get(fanpageRef);
          if (snapshot.exists()) {
            const data = snapshot.val();
            setFanpageInfo(data.fanpageInfo || "");
            setField(data.field || "");
          } else {
            setFanpageInfo("");
            setField("");
          }
        } catch (error) {
          console.error("Error fetching fanpage data:", error);
        }
      };

      fetchFanpageData();
    }
  }, [editingFanpageId]);

  // Kiểm tra trạng thái token khi component được mount (có cache 1 ngày)
  useEffect(() => {
    const autoCheckAndRefreshTokens = async () => {
      // Kiểm tra cache: nếu đã check token trong vòng 1 ngày → skip
      const oneDayInMs = 24 * 60 * 60 * 1000;
      const lastCheckTime = parseInt(
        localStorage.getItem("lastFanpageTokenCheckTime") || "0",
        10,
      );
      const timeSinceLastCheck = Date.now() - lastCheckTime;

      if (lastCheckTime > 0 && timeSinceLastCheck < oneDayInMs) {
        console.log(
          `⏳ Token đã kiểm tra ${Math.round(timeSinceLastCheck / (60 * 1000))} phút trước, chưa đủ 1 ngày → skip auto-check`,
        );
        return;
      }

      console.log(
        "🔄 Bắt đầu auto-check token fanpages (đã >= 1 ngày hoặc chưa từng check)...",
      );
      setIsLoading(true);
      setAutoConnectStats((prev) => ({ ...prev, inProgress: true }));

      try {
        // Kiểm tra token của tất cả fanpage
        const result = await checkAllFanpageTokens();

        if (result.success) {
          // Chuyển đổi kết quả thành mảng để dễ hiển thị
          const statusArray = Object.entries(result.results || {}).map(
            ([id, status]) => ({
              id,
              ...status,
            }),
          );

          setFanpageStatuses(statusArray);

          // Nếu có token không hợp lệ, thử tự động làm mới
          if (result.hasInvalidTokens) {
            // Lấy danh sách fanpage có token không hợp lệ
            const invalidFanpages = statusArray.filter((page) => !page.isValid);

            // Cập nhật số lượng fanpage cần kết nối lại
            setAutoConnectStats((prev) => ({
              ...prev,
              total: invalidFanpages.length,
              success: 0,
              failed: 0,
            }));

            // Thử tự động làm mới token cho từng fanpage
            let successCount = 0;
            let failedCount = 0;

            for (const fanpage of invalidFanpages) {
              setProcessingFanpageId(fanpage.id);

              try {
                const userId = localStorage.getItem("userId");
                if (userId) {
                  // Thử làm mới token tự động
                  const refreshResult = await checkAndRefreshFanpageToken(
                    userId,
                    fanpage.id,
                  );

                  if (refreshResult.success) {
                    successCount++;
                    setAutoConnectStats((prev) => ({
                      ...prev,
                      success: prev.success + 1,
                    }));

                    // Cập nhật trạng thái fanpage trong fanpageStatuses
                    setFanpageStatuses((prev) => {
                      const updatedStatuses = prev.map((p) =>
                        p.id === fanpage.id
                          ? { ...p, isValid: true, tokenStatus: "active" }
                          : p,
                      );

                      // Cập nhật combinedFanpageData để đảm bảo hiển thị đúng trạng thái
                      setCombinedFanpageData((prevCombined) =>
                        prevCombined.map((p) =>
                          p.id === fanpage.id
                            ? { ...p, isValid: true, tokenStatus: "active" }
                            : p,
                        ),
                      );

                      return updatedStatuses;
                    });
                  } else {
                    failedCount++;
                    setAutoConnectStats((prev) => ({
                      ...prev,
                      failed: prev.failed + 1,
                    }));
                  }
                }
              } catch (error) {
                console.error(
                  `Error auto-refreshing token for ${fanpage.name}:`,
                  error,
                );
                failedCount++;
                setAutoConnectStats((prev) => ({
                  ...prev,
                  failed: prev.failed + 1,
                }));
              }
            }

            // Cập nhật lại trạng thái sau khi thử làm mới tất cả
            const updatedResult = await checkAllFanpageTokens();
            if (updatedResult.success) {
              const updatedStatusArray = Object.entries(
                updatedResult.results || {},
              ).map(([id, status]) => ({
                id,
                ...status,
              }));
              setFanpageStatuses(updatedStatusArray);
            }

            // Hiển thị thông báo tổng hợp
            if (successCount > 0) {
              toast.success(
                `Đã tự động kết nối lại ${successCount}/${invalidFanpages.length} fanpage thành công!`,
                {
                  position: "top-right",
                  autoClose: 5000,
                  toastId: "fanpage-auto-reconnect-success",
                },
              );
            }

            if (failedCount > 0) {
              toast.warning(
                `Có ${failedCount} fanpage cần kết nối lại thủ công. Nhấn nút "Kết nối tự động" để thử lại.`,
                {
                  position: "top-right",
                  autoClose: 5000,
                  toastId: "fanpage-manual-reconnect-needed",
                },
              );
            }
          } else {
            // Nếu tất cả token đều hợp lệ
          }
        }
      } catch (error) {
        console.error("Error in auto check and refresh:", error);
      } finally {
        // Lưu timestamp check thành công vào localStorage
        localStorage.setItem(
          "lastFanpageTokenCheckTime",
          Date.now().toString(),
        );
        console.log("✅ Đã lưu lastFanpageTokenCheckTime vào localStorage");
        setIsLoading(false);
        setProcessingFanpageId(null);
        setAutoConnectStats((prev) => ({ ...prev, inProgress: false }));
      }
    };

    autoCheckAndRefreshTokens();
  }, []);

  // Theo dõi sự thay đổi của refreshTrigger để cập nhật trạng thái fanpage
  useEffect(() => {
    // Nếu refreshTrigger > 0, tức là đã có yêu cầu làm mới từ component cha
    if (refreshTrigger > 0) {
      console.log(
        "Nhận được yêu cầu làm mới trạng thái fanpage từ ProfileScreen",
      );
      // Gọi hàm kiểm tra token để cập nhật trạng thái mới nhất
      checkTokens();
    }
  }, [refreshTrigger]);

  // Hàm kiểm tra trạng thái token của tất cả fanpage (manual - không cache)
  // silent: nếu true thì không hiển thị toast
  const checkTokens = async ({ silent = false } = {}) => {
    // Xóa cache timestamp để force check ngay lập tức
    localStorage.removeItem("lastFanpageTokenCheckTime");
    setIsLoading(true);
    try {
      const result = await checkAllFanpageTokens();

      if (result.success) {
        // Chuyển đổi kết quả thành mảng để dễ hiển thị
        const statusArray = Object.entries(result.results || {}).map(
          ([id, status]) => ({
            id,
            ...status,
          }),
        );

        // Cập nhật fanpageStatuses và đảm bảo combinedFanpageData cũng được cập nhật
        setFanpageStatuses(statusArray);

        // Cập nhật combinedFanpageData dựa trên statusArray mới
        if (connectedFanpages.length > 0) {
          // Tạo map từ statusArray để dễ truy cập
          const statusMap = statusArray.reduce((map, status) => {
            map[status.id] = status;
            return map;
          }, {});

          // Cập nhật combinedFanpageData với thông tin trạng thái mới nhất
          setCombinedFanpageData((prevCombined) =>
            prevCombined.map((page) => {
              const status = statusMap[page.id];
              if (status) {
                return {
                  ...page,
                  isValid: status.isValid,
                  tokenStatus: status.isValid ? "active" : "invalid",
                  error: status.error,
                };
              }
              return page;
            }),
          );
        }

        // Hiển thị thông báo nếu có token không hợp lệ và không đang trong quá trình tự động kết nối
        if (!silent && result.hasInvalidTokens && !autoConnectStats.inProgress) {
          toast.warning(
            'Một số fanpage cần được kết nối lại. Nhấn nút "Kết nối tự động" để khôi phục kết nối.',
            {
              position: "top-right",
              autoClose: 3000,
              toastId: "fanpage-reconnect-warning",
            },
          );
        } else if (!silent && !result.hasInvalidTokens && !autoConnectStats.inProgress) {
          // Chỉ hiển thị thông báo thành công nếu không đang trong quá trình tự động kết nối
          // và tất cả token đều hợp lệ
          toast.success("Tất cả fanpage đã được kết nối thành công!", {
            position: "top-right",
            autoClose: 3000,
            toastId: "fanpage-all-connected",
          });
        }
      } else {
        // Chỉ hiển thị thông báo lỗi nếu không đang trong quá trình tự động kết nối và không silent
        if (!silent && !autoConnectStats.inProgress) {
          toast.error(`Không thể kiểm tra token: ${result.error}`, {
            position: "top-right",
            autoClose: 5000,
            toastId: "fanpage-token-check-error",
          });
        }
      }
    } catch (error) {
      console.error("Error checking tokens:", error);
      // Chỉ hiển thị thông báo lỗi nếu không đang trong quá trình tự động kết nối và không silent
      if (!silent && !autoConnectStats.inProgress) {
        toast.error("Đã xảy ra lỗi khi kiểm tra token", {
          position: "top-right",
          autoClose: 5000,
          toastId: "fanpage-token-error",
        });
      }
    } finally {
      // Lưu timestamp để cache kết quả check
      localStorage.setItem("lastFanpageTokenCheckTime", Date.now().toString());
      setIsLoading(false);
    }
  };

  // Hàm xử lý khi nhấn nút kết nối tự động
  const handleAutoReconnect = async (fanpage) => {
    setProcessingFanpageId(fanpage.id);
    // Đánh dấu đang trong quá trình tự động kết nối
    setAutoConnectStats((prev) => ({ ...prev, inProgress: true }));

    try {
      const userId = localStorage.getItem("userId");
      if (!userId) {
        toast.error("Bạn cần đăng nhập lại để thực hiện thao tác này", {
          position: "top-right",
          autoClose: 5000,
          toastId: "fanpage-login-required",
        });
        return;
      }

      // Thử làm mới token tự động
      const result = await checkAndRefreshFanpageToken(userId, fanpage.id);

      if (result.success) {
        toast.success(`Đã kết nối lại fanpage ${fanpage.name} thành công!`, {
          position: "top-right",
          autoClose: 3000,
        });

        // Cập nhật trạng thái fanpage trong fanpageStatuses
        setFanpageStatuses((prev) => {
          const updatedStatuses = prev.map((p) =>
            p.id === fanpage.id
              ? { ...p, isValid: true, tokenStatus: "active" }
              : p,
          );

          // Cập nhật combinedFanpageData để đảm bảo hiển thị đúng trạng thái
          setCombinedFanpageData((prevCombined) =>
            prevCombined.map((p) =>
              p.id === fanpage.id
                ? { ...p, isValid: true, tokenStatus: "active" }
                : p,
            ),
          );

          return updatedStatuses;
        });

        // Cập nhật lại trạng thái token (silent: không hiển thị thông báo)
        await checkTokens({ silent: true });
      } else {
        // Nếu không thể tự động làm mới, hiển thị modal để kết nối thủ công
        setSelectedFanpage(fanpage);
        setShowReconnectModal(true);
      }
    } catch (error) {
      console.error("Error auto reconnecting fanpage:", error);
      toast.error(
        "Không thể tự động kết nối lại. Vui lòng thử kết nối thủ công.",
        {
          position: "top-right",
          autoClose: 5000,
          toastId: "fanpage-auto-reconnect-failed",
        },
      );

      // Hiển thị modal kết nối thủ công
      setSelectedFanpage(fanpage);
      setShowReconnectModal(true);
    } finally {
      setProcessingFanpageId(null);
      // Đánh dấu đã hoàn thành quá trình tự động kết nối
      setAutoConnectStats((prev) => ({ ...prev, inProgress: false }));
    }
  };

  // Hàm xử lý khi xác nhận kết nối lại thủ công
  const handleConfirmReconnect = async () => {
    setShowReconnectModal(false);
    setIsLoading(true);
    // Đánh dấu đang trong quá trình tự động kết nối
    setAutoConnectStats((prev) => ({ ...prev, inProgress: true }));

    try {
      const result = await reconnectFanpage(selectedFanpage);

      if (result.success) {
        toast.success(result.message, {
          position: "top-right",
          autoClose: 3000,
        });

        // Cập nhật trạng thái fanpage trong fanpageStatuses
        setFanpageStatuses((prev) => {
          const updatedStatuses = prev.map((p) =>
            p.id === selectedFanpage.id
              ? { ...p, isValid: true, tokenStatus: "active" }
              : p,
          );

          // Cập nhật combinedFanpageData để đảm bảo hiển thị đúng trạng thái
          setCombinedFanpageData((prevCombined) =>
            prevCombined.map((p) =>
              p.id === selectedFanpage.id
                ? { ...p, isValid: true, tokenStatus: "active" }
                : p,
            ),
          );

          return updatedStatuses;
        });

        // Cập nhật lại trạng thái token (silent: không hiển thị thông báo)
        await checkTokens({ silent: true });
      } else {
        toast.error(`Kết nối lại không thành công: ${result.error}`, {
          position: "top-right",
          autoClose: 5000,
        });
      }
    } catch (error) {
      console.error("Error reconnecting fanpage:", error);
      toast.error("Đã xảy ra lỗi khi kết nối lại fanpage", {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setIsLoading(false);
      // Đánh dấu đã hoàn thành quá trình tự động kết nối
      setAutoConnectStats((prev) => ({ ...prev, inProgress: false }));
    }
  };

  // Hàm xử lý cấp thêm quyền bình luận cho fanpage
  const handleGrantCommentPermissions = async (page) => {
    setProcessingFanpageId(page.id);
    setIsLoading(true);
    try {
      const result = await grantAdditionalCommentPermissions(page);
      if (result.success) {
        toast.success(result.message, {
          position: "top-right",
          autoClose: 3000,
        });
        // Cập nhật lại trạng thái token
        await checkTokens({ silent: true });
      } else {
        toast.error(`Không thể nâng cấp quyền: ${result.error}`, {
          position: "top-right",
          autoClose: 5000,
        });
      }
    } catch (error) {
      console.error("Lỗi nâng cấp quyền bình luận:", error);
      toast.error("Đã xảy ra lỗi khi nâng cấp quyền bình luận.", {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setProcessingFanpageId(null);
      setIsLoading(false);
    }
  };

  // Hàm xử lý khi nhấn nút chỉnh sửa fanpage
  const handleEditFanpage = (page) => {
    setEditingFanpageId(page.id);
  };

  // Hàm xử lý khi lưu thông tin fanpage
  const handleSaveFanpageInfo = async () => {
    if (!editingFanpageId) return;

    setIsSaving(true);
    try {
      const userId = localStorage.getItem("userId");
      const db = getDatabase();
      const fanpageRef = ref(
        db,
        `users/${userId}/fanpages/${editingFanpageId}`,
      );

      // Lấy tên fanpage từ danh sách
      const selectedFanpageName = combinedFanpageData.find(
        (page) => page.id === editingFanpageId,
      )?.name;

      await set(fanpageRef, {
        fanpageName: selectedFanpageName,
        fanpageInfo,
        field,
        updatedAt: new Date().toISOString(),
      });

      toast.success("Lưu thông tin fanpage thành công!", {
        position: "top-right",
        autoClose: 3000,
      });

      // Đóng form chỉnh sửa
      setEditingFanpageId(null);
    } catch (error) {
      console.error("Error saving fanpage data:", error);
      toast.error("Lưu thông tin thất bại: " + error.message, {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Hàm hủy chỉnh sửa
  const handleCancelEdit = () => {
    setEditingFanpageId(null);
    setFanpageInfo("");
    setField("");
  };

  // Hàm định dạng ngày hết hạn
  const formatExpirationDate = (timestamp) => {
    if (!timestamp) return "Không có";

    let date;
    if (typeof timestamp === "number") {
      if (timestamp > 100000000000) {
        date = new Date(timestamp);
      } else {
        date = new Date(timestamp * 1000);
      }
    } else if (typeof timestamp === "string") {
      date = new Date(timestamp);
    } else if (timestamp instanceof Object && timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      return "Không xác định";
    }

    if (isNaN(date.getTime())) {
      return "Không xác định";
    }

    return date.toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getMissingCommentScopes = (page) =>
    FACEBOOK_COMMENT_REQUIRED_SCOPES.filter(
      (scope) => !(page.grantedScopes || []).includes(scope),
    );

  return (
    <div className="bg-white rounded-lg shadow p-4 mb-6">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center">
          <h2 className="text-lg font-semibold">Trạng thái kết nối Fanpage</h2>
          <button
            className="ml-2 text-gray-500 hover:text-gray-700"
            onClick={() => setShowInfo(!showInfo)}
          >
            <FaInfoCircle />
          </button>
        </div>
        <button
          onClick={checkTokens}
          disabled={isLoading}
          className="flex items-center px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
        >
          {isLoading ? (
            <FaSync className="animate-spin mr-1" />
          ) : (
            <FaSync className="mr-1" />
          )}
          Kiểm tra lại
        </button>
      </div>

      {showInfo && (
        <div className="mb-4 p-3 bg-blue-50 text-blue-800 rounded-md">
          <p className="text-sm">
            Facebook yêu cầu làm mới quyền truy cập định kỳ (thường là 60 ngày)
            để đảm bảo an toàn. Khi token hết hạn, bạn có thể nhấn nút "Kết nối
            tự động" để hệ thống tự động khôi phục kết nối. Nếu không thành
            công, hệ thống sẽ hướng dẫn bạn kết nối thủ công.
          </p>
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-4 bg-blue-50 rounded-md mb-4">
          <FaSync className="animate-spin mr-2 text-blue-500" />
          <p className="text-blue-700">
            Đang tự động kiểm tra và kết nối lại fanpage...
          </p>
        </div>
      )}

      {combinedFanpageData.length === 0 ? (
        <p className="text-gray-500 text-center py-4">
          {isLoading ? "Đang kiểm tra..." : "Không có fanpage nào được kết nối"}
        </p>
      ) : (
        <div className="space-y-4">
          {combinedFanpageData.map((page) => (
            <div
              key={page.id}
              className={`border border-gray-200 rounded-md p-4 hover:bg-gray-50 ${
                page.isValid === false ? "bg-red-50" : ""
              }`}
            >
              {editingFanpageId === page.id ? (
                <div className="bg-blue-50 p-4 rounded-md mb-2">
                  <div className="mb-4">
                    <label className="block text-gray-700 font-medium mb-2">
                      Thông tin Fanpage
                    </label>
                    <textarea
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 textarea"
                      value={fanpageInfo}
                      onChange={(e) => setFanpageInfo(e.target.value)}
                      placeholder="Thông tin về Fanpage ở cuối bài viết"
                      rows={4}
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-gray-700 font-medium mb-2">
                      Lĩnh vực
                    </label>
                    <input
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={field}
                      onChange={(e) => setField(e.target.value)}
                      placeholder="Thời trang, bất động sản,.."
                    />
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleSaveFanpageInfo}
                      disabled={isSaving}
                      className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600 disabled:opacity-50 flex items-center"
                    >
                      {isSaving ? (
                        <>
                          <FaSync className="animate-spin mr-1" />
                          Đang lưu...
                        </>
                      ) : (
                        <>
                          <FaSave className="mr-1" />
                          Lưu thông tin
                        </>
                      )}
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="px-3 py-1 bg-gray-500 text-white text-sm rounded hover:bg-gray-600 flex items-center"
                    >
                      <FaTimes className="mr-1" />
                      Hủy
                    </button>
                  </div>
                </div>
              ) : null}

              <div className="flex justify-between items-center">
                <div>
                  <div className="flex items-center">
                    {page.isValid === false ? (
                      processingFanpageId === page.id ? (
                        <FaSync className="animate-spin text-blue-500 mr-2" />
                      ) : (
                        <FaExclamationCircle className="text-red-500 mr-2" />
                      )
                    ) : (
                      <FaCheckCircle className="text-green-500 mr-2" />
                    )}
                    <h3 className="font-medium text-gray-800 text-lg">
                      {page.name}
                    </h3>
                    {processingFanpageId === page.id && (
                      <span className="ml-2 text-xs text-blue-600">
                        Đang kết nối lại...
                      </span>
                    )}
                  </div>
                  <p className="text-gray-500 text-sm">
                    {page.category || "Fanpage Facebook"}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${
                        page.commentAutomationEligible
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      {page.commentAutomationEligible
                        ? "✓ AI comment ready"
                        : "⚠️ Thiếu quyền AI comment"}
                    </span>
                    {page.webhookSubscribed && (
                      <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-700">
                        Webhook subscribed
                      </span>
                    )}
                  </div>
                  {!page.commentAutomationEligible &&
                    getMissingCommentScopes(page).length > 0 && (
                      <p className="text-xs text-amber-700 mt-2">
                        Thiếu quyền: {getMissingCommentScopes(page).join(", ")}
                      </p>
                    )}
                  <div className="flex items-center mt-2">
                    <span
                      className={`inline-block w-2 h-2 rounded-full mr-2 ${
                        page.isValid === false
                          ? "bg-red-500"
                          : page.tokenStatus === "active"
                          ? "bg-green-500"
                          : page.tokenStatus === "error"
                          ? "bg-red-500"
                          : "bg-yellow-500"
                      }`}
                    ></span>
                    <p className="text-xs text-gray-500">
                      {processingFanpageId === page.id
                        ? "Đang xử lý token..."
                        : page.isValid === false
                        ? "Token không hợp lệ"
                        : page.tokenStatus === "active"
                        ? "Đang hoạt động"
                        : page.tokenStatus === "error"
                        ? "Token lỗi"
                        : "Trạng thái không xác định"}
                      {page.tokenExpiry &&
                        ` - Hết hạn: ${formatExpirationDate(page.tokenExpiry)}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <a
                    href={`https://facebook.com/${page.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-blue-600 hover:bg-blue-100 rounded"
                  >
                    <FaExternalLinkAlt className="h-5 w-5" />
                  </a>

                  <button
                    onClick={() => handleEditFanpage(page)}
                    disabled={editingFanpageId !== null || isLoading}
                    className="p-2 text-blue-600 hover:bg-blue-100 rounded disabled:opacity-50"
                  >
                    <FaEdit className="h-5 w-5" />
                  </button>

                  {page.isValid !== false && page.commentAutomationEligible !== true && (
                    <button
                      onClick={() => handleGrantCommentPermissions(page)}
                      disabled={processingFanpageId === page.id || isLoading}
                      className="px-3 py-1 bg-amber-500 text-white text-sm rounded hover:bg-amber-600 disabled:opacity-50 flex items-center transition-colors shadow-sm"
                    >
                      {processingFanpageId === page.id ? (
                        <>
                          <FaSync className="animate-spin mr-1" />
                          Đang xử lý...
                        </>
                      ) : (
                        "Cấp thêm quyền"
                      )}
                    </button>
                  )}

                  {page.isValid === false && (
                    <button
                      onClick={() => handleAutoReconnect(page)}
                      disabled={processingFanpageId === page.id || isLoading}
                      className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 disabled:opacity-50 flex items-center"
                    >
                      {processingFanpageId === page.id ? (
                        <>
                          <FaSync className="animate-spin mr-1" />
                          Đang xử lý...
                        </>
                      ) : (
                        <>
                          <FaSync className="mr-1" />
                          Kết nối tự động
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ReconnectFanpageModal
        isOpen={showReconnectModal}
        onClose={() => setShowReconnectModal(false)}
        fanpage={selectedFanpage}
        onReconnect={handleConfirmReconnect}
      />
    </div>
  );
};

export default FanpageTokenManager;
